<template>
  <div
    class="group flex items-start gap-4 rounded-2xl bg-base-100/90 p-4 text-left shadow-sm transition-all duration-200"
    :class="{
      'my-2 border-jade-300/70 bg-jade-300/10 shadow-md': props.highlight,
      'hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/5 hover:shadow-md': !props.highlight && !isWebinarContent,
      'border-emerald-300/90 bg-emerald-50/60 shadow-md shadow-emerald-100/40': isWebinarContent && isUpcomingWebinar,
      'border-slate-300/90 bg-slate-50/70': isWebinarContent && !isUpcomingWebinar
    }"
  >
    <template v-if="showPreviewImage">
      <div :style="previewImage" class="mt-1 h-10 aspect-square rounded-lg bg-contain"></div>
    </template>
    <div class="grow min-w-0 text-left">
      <template v-if="!isBonusReport">
        <div class="flex justify-between gap-3">
          <h3 @click="openContent" class="mb-0 line-clamp-2 cursor-pointer text-lg leading-snug">{{ title }}</h3>
          <div
            v-if="isAuthenticatedArticleOrWebinar"
            @click.prevent="handleFavoritedContentAction( content?.contentfulId, contentType )"
            class="tooltip inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-base-300/70 bg-base-100 text-base-content/70 transition hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
            :data-tip="dataTipForFavoritedStatus( content?.contentfulId, contentType )"
          >
            <i class="pi text-sm" :class="favoriteIconClassNameForContent( content?.contentfulId, contentType )"></i>
          </div>
        </div>
        <div class="mt-1 self-start text-sm text-base-content/70">
          <template v-if="showAuthor">By <router-link :to="authorRoute" class="text-base-content"> {{ authorName }} </router-link></template>
          <template v-if="showProductTitle">
            <span>&nbsp;in&nbsp;</span>
            <router-link v-if="isProductDisplayable" :to="productRoute" class="text-base-content">
              {{ selectedOrPrimaryProduct?.name }}
            </router-link>
            <span v-else class="text-base-content">
              {{ selectedOrPrimaryProduct?.name }}
            </span>
          </template>
        </div>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <span
            v-if="isWebinarContent"
            class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide"
            :class="isUpcomingWebinar ? 'border-emerald-400/90 bg-emerald-200/80 text-emerald-800' : 'border-slate-400/90 bg-slate-200/80 text-slate-700'"
          >
            <i class="pi text-[0.6rem]" :class="isUpcomingWebinar ? 'pi-bolt' : 'pi-history'"></i>
            {{ isUpcomingWebinar ? 'Upcoming Webinar' : 'Past Webinar' }}
          </span>
          <span
            v-if="webinarDateLabel && isWebinarContent"
            class="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
            :class="isUpcomingWebinar ? 'border-emerald-300/80 bg-base-100/90 text-emerald-800' : 'border-slate-300/80 bg-base-100/90 text-slate-700'"
          >
            {{ isUpcomingWebinar ? 'Starts' : 'Aired' }}: {{ webinarDateLabel }}
          </span>
          <span v-if="!!estimatedReadingTime && !isWebinarContent" class="inline-flex items-center rounded-full border border-base-300/70 bg-base-200/50 px-2 py-0.5 text-xs text-base-content/70">
            {{ estimatedReadingTime }}
          </span>
        </div>
        <div class="mt-3 text-sm">
          <div v-if="previewText" @click="openContent" class="line-clamp-3 cursor-pointer text-base-content/75">{{ previewText }}</div>
          <div v-if="showTags">
            <Tags :tags-list="tagOptions" :slug="slug" />
          </div>
          <div class="mt-4 flex items-end justify-between">
            <div v-if="metaFooterText" class="text-xs text-base-content/55">
              {{ metaFooterText }}
            </div>
            <div @click="openContent" class="btn btn-sm btn-primary">
              {{ isWebinarContent ? 'View webinar' : 'Read more' }}
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex justify-between">
          <div>
            <h3 @click.prevent="openBonusReport()" class="cursor-pointer mb-0">{{ title }}</h3>
            <div class="text-sm mt-1">
              <template v-if="showAuthor">by <router-link :to="authorRoute" class="text-base-content"> {{ authorName }} </router-link></template>
              <template v-if="showProductTitle"> in <router-link :to="productRoute" class="text-base-content"> {{ selectedOrPrimaryProduct?.name }} </router-link></template>
            </div>
          </div>
          <div @click.prevent="openBonusReport()" class="btn btn-primary">Get Report</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";

import * as tmi from "@/constants";

import Tags from '@/components/content/Tags.vue';

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import { useContentActions } from "@/composables/content-actions";

import routeManager from "@/router/route-manager";

import { type Author, ContentfulItem, type HasAuthor, type Product, type Tag, type Webinar } from "@tmi/types/entities";
import type { SearchContextWrapper } from "@tmi/types/context/search-context";

import { articleKey, bonusReportKey, webinarKey } from "@/types/products/product-definitions";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";
import { countTime, formatDateAsMonthDayYearString } from "@tmi/utils/date-time";

const route = useRoute();
const props = defineProps< { slug: string, contentType: string, highlight?: boolean } >();
const { isAuthenticated } = storeToRefs( useAuthStore() );

const injectedContext = inject< SearchContextWrapper | undefined >( tmi.searchContextSymbol );
if ( !injectedContext ) {
  throw new Error( `The expected provided value for 'searchContextSymbol' does not exist in this context.` );
}

const { contentBySlug, setSelectedContent } = injectedContext!;
const userStore = useUserStore();

const content = ref< ContentfulItem | undefined >( contentBySlug( props.slug ) );
const authoredContent = ref< HasAuthor | undefined >( content.value as HasAuthor ? content.value as HasAuthor : undefined );
const contentModelType = ref< string | undefined >( props.contentType );
const contentText = computed< string >( () => ( authoredContent.value?.text ?? '' ).trim() );

const isAuthenticatedArticleOrWebinar = computed< boolean >( () => {
  const authenticated = isAuthenticated.value
  const isArticleOrWebinar = ( !!contentModelType.value &&
      ( contentModelType.value.includes( articleKey ) || contentModelType.value.includes( webinarKey ) ) );

  const canBeFavorited = isFavoritable( content.value );

  return authenticated && isArticleOrWebinar && canBeFavorited;
} );

const isBonusReport = computed< boolean >( () => contentModelType.value === bonusReportKey );

const productSlug = computed< string | undefined >( () => route.params.productSlug ? `${ route.params.productSlug }` : undefined );
const showProductTitle = computed< boolean >( () => !!( isAuthenticated.value && selectedOrPrimaryProduct.value && title.value ) );

const selectedOrPrimaryProduct = computed< Product | undefined >( () => productSlug.value ? content.value?.products.find( ( p: Product ) => p.slug === productSlug.value ) : content.value?.products[ 0 ] );
const productRoute = computed< string >( () => !!selectedOrPrimaryProduct ? routeManager.productRouteBySlug( selectedOrPrimaryProduct.value!.slug ) : '#' );
const isProductDisplayable = computed< boolean | undefined >( () => selectedOrPrimaryProduct.value?.isDisplay );

const showAuthor = computed< boolean >( () => !!( author.value ) );
const author = computed< Author | null | undefined >( () => authoredContent.value?.author ?? undefined );
const authorName = computed< string >( () => author.value?.name ?? '---' );
const authorRoute = computed< string >( () => !!( author.value?.slug ) ? `${ tmi.AuthorsPath }/${ author.value?.slug }` : '#' );

const previewText = computed< string >( () => {
  // Remove any HTML tags within the main content and display the first 255 characters
  const domElement = document.createElement( 'div' );
  domElement.innerHTML = contentText.value;
  const trimmedString = `${ domElement.innerText.slice( 0, 200 ).trim() }`;
  return !!( trimmedString ) ? `${ trimmedString }...` : '';
} );

const title = computed< string | undefined >( () => content.value?.title );
const slug = computed< string | undefined >( () => content.value?.slug );
const isWebinarContent = computed< boolean >( () => !!contentModelType.value && contentModelType.value.includes( webinarKey ) );
const webinarDate = computed< string | Date | undefined >( () => ( content.value as Webinar | undefined )?.webinarDate );
const isUpcomingWebinar = computed< boolean >( () => {
  if ( !isWebinarContent.value || !webinarDate.value ) {
    return false;
  }

  return new Date( webinarDate.value ).getTime() >= Date.now();
} );

const publishedDate = ref< string >( formatDateAsMonthDayYearString( content.value?.publishedDate ) );
const webinarDateLabel = computed< string >( () => formatDateAsMonthDayYearString( webinarDate.value ) );
const estimatedReadingTime = computed< string >( () => countTime( authoredContent.value?.text ?? '' ) );
const metaFooterText = computed< string >( () => {
  if ( isWebinarContent.value ) {
    return '';
  }

  return publishedDate.value;
} );

const previewImage = computed( () => `background-image: url(${ favicon.value })` );
const favicon = computed< string | undefined >( () => selectedOrPrimaryProduct.value?.favicon );
const showPreviewImage = computed< boolean >( () => !!favicon );

const tagOptions: Tag[] = [ ...( content.value?.tags ?? [] ) ].map( ( tag: Tag ) => ( { ...tag, label: tag.slug } ) );
const showTags = ref< boolean >( tagOptions.length > 0 );

function openContent() {
  if ( content.value?.contentfulId ) {
    setSelectedContent( content.value!.contentfulId );
  }
}

function openBonusReport() {
  handleOpenContentInNewTab( content.value, props.contentType );
  reportContentViewEvent( content.value, props.contentType );
}

const {
  dataTipForFavoritedStatus,
  favoriteIconClassNameForContent,
  handleFavoritedContentAction,
  handleOpenContentInNewTab,
  isFavoritable,
} = useContentActions();
</script>
